import { ArrowRight } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';

import { ResolvedReferenceText } from '@apps/web/features/phoebe_chat_v3/ui/primitives/reference_badge';

function FromToBlocks({
    from,
    to,
}: Readonly<{ from: string; to: string }>): ReactNode {
    return (
        <div className="text-v3-chat inline-flex max-w-full gap-1.5 align-top">
            {(
                [
                    ['From', from],
                    ['To', to],
                ] as const
            ).map(([label, value]) => (
                <Fragment key={label}>
                    {label === 'To' && (
                        <ArrowRight
                            aria-hidden={true}
                            className="text-muted-foreground size-4 shrink-0 self-center"
                        />
                    )}
                    <dl className="bg-muted/50 m-0 min-w-0 flex-initial rounded-md px-2 py-1">
                        <dt className="text-muted-foreground">{label}</dt>
                        <dd className="text-foreground m-0 whitespace-pre-wrap">
                            <ResolvedReferenceText text={value} />
                        </dd>
                    </dl>
                </Fragment>
            ))}
        </div>
    );
}

/** Format the backend's display copy; preserve text that has no unambiguous pair. */
export function ApprovalChangeDetails({
    items,
    settingsChanges,
}: Readonly<{
    items: ReadonlyArray<string>;
    settingsChanges: boolean;
}>): ReactNode {
    const fromItems = items.filter((item) => item.startsWith('From: '));
    const toItems = items.filter((item) => item.startsWith('To: '));
    const from = fromItems.length === 1 ? fromItems[0] : undefined;
    const to = toItems.length === 1 ? toItems[0] : undefined;
    const paired = from !== undefined && to !== undefined;
    return (
        <div className="text-v3-chat space-y-2">
            {items.map((item, index) => {
                if (!settingsChanges && paired && item === to) {
                    return null;
                }
                if (!settingsChanges && paired && item === from) {
                    return (
                        <FromToBlocks
                            key={index}
                            from={from.slice(6)}
                            to={to.slice(4)}
                        />
                    );
                }
                // The settings presenter emits “Field: before → after”.
                // Multiple arrows may be part of a text value, so leave those intact.
                const parts = settingsChanges ? item.split(' → ') : [];
                const separator = parts[0]?.indexOf(': ') ?? -1;
                if (parts.length === 2 && separator > 0) {
                    const label = parts[0]!.slice(0, separator);
                    const before = parts[0]!.slice(separator + 2);
                    const after = parts[1]!;
                    if (before.length > 0 && after.length > 0) {
                        return (
                            <div key={index}>
                                <p className="text-foreground m-0 mb-1.5 font-medium">
                                    <ResolvedReferenceText text={label} />
                                </p>
                                <FromToBlocks from={before} to={after} />
                            </div>
                        );
                    }
                }
                return (
                    <p
                        key={index}
                        className="text-muted-foreground m-0 whitespace-pre-wrap"
                    >
                        <ResolvedReferenceText text={item} />
                    </p>
                );
            })}
        </div>
    );
}
